import { isBrokerPersona } from '../constants/personas.js';
import { onboardingSelector } from './onboardingConfig.js';
import { resolvePersonaCopy } from './onboardingAssistant.js';

/**
 * @typedef {import('driver.js').DriveStep} DriveStep
 * @typedef {{ id: string, titleKey: string, descriptionKey: string, personaDescriptionKey?: Record<string, string>, personaTitleKey?: Record<string, string> }} StepDefinition
 */

/**
 * @param {StepDefinition} stepDef
 * @param {string | undefined} persona
 * @param {(key: string) => string} t
 * @returns {{ title: string, description: string }}
 */
export function resolveStepCopy(stepDef, persona, t) {
  const personaDescOverride = persona && stepDef.personaDescriptionKey?.[persona];
  const personaTitleOverride = persona && stepDef.personaTitleKey?.[persona];

  if (personaDescOverride || personaTitleOverride) {
    return {
      title: t(personaTitleOverride || stepDef.titleKey),
      description: t(personaDescOverride || stepDef.descriptionKey),
    };
  }

  return resolvePersonaCopy(stepDef.titleKey, stepDef.descriptionKey, persona, t);
}

/**
 * @param {StepDefinition} stepDef
 * @param {string | undefined} elementSelector
 * @param {string | undefined} persona
 * @param {(key: string) => string} t
 * @param {Partial<DriveStep>} [overrides]
 * @returns {DriveStep}
 */
function buildStep(stepDef, elementSelector, persona, t, overrides = {}) {
  const copy = resolveStepCopy(stepDef, persona, t);
  const { popover: popoverOverrides, ...restOverrides } = overrides;
  /** @type {DriveStep} */
  const step = {
    ...restOverrides,
    popover: {
      title: copy.title,
      description: copy.description,
      side: 'bottom',
      align: 'center',
      ...(popoverOverrides || {}),
    },
  };
  if (elementSelector) {
    step.element = elementSelector;
  }
  return step;
}

/**
 * @returns {number}
 */
function countVisibleHomeTabs() {
  if (typeof document === 'undefined') return 0;
  return document.querySelectorAll(`${onboardingSelector('home-tabs')} button`).length;
}

/**
 * @param {DriveStep[]} steps
 * @returns {DriveStep[]}
 */
export function filterStepsWithExistingElements(steps) {
  if (typeof document === 'undefined') return steps;
  return steps.filter((step) => {
    if (!step.element) return true;
    return Boolean(document.querySelector(String(step.element)));
  });
}

/**
 * @param {{ persona?: string, t: (key: string) => string, hasProperties?: boolean, isOwned?: boolean }} ctx
 * @returns {DriveStep[]}
 */
export function getWelcomeSteps(ctx) {
  const { persona, t, hasProperties = false } = ctx;
  const isBroker = isBrokerPersona(persona);
  const createSelector = hasProperties
    ? onboardingSelector('home-new-deal')
    : onboardingSelector('home-empty-cta');
  const showTabsStep = countVisibleHomeTabs() > 1;

  const steps = [
    buildStep(
      {
        id: 'intro',
        titleKey: 'tour.welcome.intro.title',
        descriptionKey: 'tour.welcome.intro.description',
      },
      undefined,
      persona,
      t,
      { popover: { side: 'over', align: 'center' } },
    ),
    buildStep(
      {
        id: 'create',
        titleKey: 'tour.welcome.create.title',
        descriptionKey: 'tour.welcome.create.description',
      },
      createSelector,
      persona,
      t,
      { popover: { side: 'top', align: 'end' }, disableActiveInteraction: false },
    ),
  ];

  if (showTabsStep) {
    steps.push(
      buildStep(
        {
          id: 'tabs',
          titleKey: 'tour.welcome.tabs.title',
          descriptionKey: 'tour.welcome.tabs.description',
        },
        onboardingSelector('home-tabs'),
        persona,
        t,
        { popover: { side: 'bottom', align: 'start' } },
      ),
    );
  }

  steps.push(
    buildStep(
      {
        id: 'profile',
        titleKey: 'tour.welcome.profile.title',
        descriptionKey: 'tour.welcome.profile.description',
      },
      onboardingSelector('header-profile'),
      persona,
      t,
      { popover: { side: 'bottom', align: 'end' } },
    ),
  );

  if (isBroker) {
    steps.push(
      buildStep(
        {
          id: 'nav',
          titleKey: 'tour.welcome.nav.title',
          descriptionKey: 'tour.welcome.nav.description',
          personaDescriptionKey: {
            preteur_prive: 'tour.welcome.nav.preteur_prive.description',
            courtier_immo: 'tour.welcome.nav.courtier_immo.description',
            courtier_hypo: 'tour.welcome.nav.courtier_hypo.description',
          },
        },
        onboardingSelector('header-nav'),
        persona,
        t,
        { popover: { side: 'bottom', align: 'start' } },
      ),
    );
  }

  return filterStepsWithExistingElements(steps);
}

/**
 * @param {{ persona?: string, t: (key: string) => string, isOwned?: boolean }} ctx
 * @returns {DriveStep[]}
 */
export function getCreateProjectSteps(ctx) {
  const { persona, t, isOwned = false } = ctx;
  const steps = [
    buildStep(
      {
        id: 'intro',
        titleKey: isOwned
          ? 'tour.createProject.owned.intro.title'
          : 'tour.createProject.intro.title',
        descriptionKey: isOwned
          ? 'tour.createProject.owned.intro.description'
          : 'tour.createProject.intro.description',
      },
      undefined,
      persona,
      t,
      { popover: { side: 'over', align: 'center' } },
    ),
    buildStep(
      {
        id: 'basic',
        titleKey: isOwned
          ? 'tour.createProject.owned.basic.title'
          : 'tour.createProject.basic.title',
        descriptionKey: isOwned
          ? 'tour.createProject.owned.basic.description'
          : 'tour.createProject.basic.description',
      },
      onboardingSelector('form-basic-info'),
      persona,
      t,
      { popover: { side: 'top', align: 'start' } },
    ),
    buildStep(
      {
        id: 'revenue',
        titleKey: 'tour.createProject.revenue.title',
        descriptionKey: 'tour.createProject.revenue.description',
      },
      onboardingSelector('form-revenue'),
      persona,
      t,
      { popover: { side: 'top', align: 'start' } },
    ),
    buildStep(
      {
        id: 'expenses',
        titleKey: 'tour.createProject.expenses.title',
        descriptionKey: 'tour.createProject.expenses.description',
      },
      onboardingSelector('form-expenses'),
      persona,
      t,
      { popover: { side: 'top', align: 'start' } },
    ),
    buildStep(
      {
        id: 'ownedFinancing',
        titleKey: 'tour.createProject.owned.financing.title',
        descriptionKey: 'tour.createProject.owned.financing.description',
      },
      onboardingSelector('form-owned-financing'),
      persona,
      t,
      { popover: { side: 'top', align: 'start' } },
    ),
    buildStep(
      {
        id: 'mode',
        titleKey: 'tour.createProject.mode.title',
        descriptionKey: 'tour.createProject.mode.description',
      },
      onboardingSelector('form-mode-toggle'),
      persona,
      t,
    ),
    buildStep(
      {
        id: 'save',
        titleKey: isOwned
          ? 'tour.createProject.owned.save.title'
          : 'tour.createProject.save.title',
        descriptionKey: isOwned
          ? 'tour.createProject.owned.save.description'
          : 'tour.createProject.save.description',
      },
      onboardingSelector('form-save'),
      persona,
      t,
      { popover: { side: 'top', align: 'end' }, disableActiveInteraction: false },
    ),
  ];
  return filterStepsWithExistingElements(steps);
}

/**
 * @param {{ persona?: string, t: (key: string) => string, isOwned?: boolean }} ctx
 * @returns {DriveStep[]}
 */
export function getDashboardSteps(ctx) {
  const { persona, t, isOwned = false } = ctx;
  const steps = [
    buildStep(
      {
        id: 'overview',
        titleKey: isOwned
          ? 'tour.dashboard.owned.overview.title'
          : 'tour.dashboard.overview.title',
        descriptionKey: isOwned
          ? 'tour.dashboard.owned.overview.description'
          : 'tour.dashboard.overview.description',
      },
      onboardingSelector('dashboard-overview'),
      persona,
      t,
      { popover: { side: 'bottom', align: 'start' } },
    ),
    buildStep(
      {
        id: 'dataRoom',
        titleKey: 'tour.dashboard.dataRoom.title',
        descriptionKey: 'tour.dashboard.dataRoom.description',
      },
      onboardingSelector('dashboard-data-room'),
      persona,
      t,
      { popover: { side: 'top', align: 'center' }, disableActiveInteraction: false },
    ),
    buildStep(
      {
        id: 'rentRegister',
        titleKey: 'tour.dashboard.rentRegister.title',
        descriptionKey: isOwned
          ? 'tour.dashboard.owned.rentRegister.description'
          : 'tour.dashboard.rentRegister.description',
      },
      onboardingSelector('dashboard-rent-register'),
      persona,
      t,
      { popover: { side: 'top', align: 'center' }, disableActiveInteraction: false },
    ),
    buildStep(
      {
        id: 'scenario',
        titleKey: isOwned
          ? 'tour.dashboard.owned.scenario.title'
          : 'tour.dashboard.scenario.title',
        descriptionKey: isOwned
          ? 'tour.dashboard.owned.scenario.description'
          : 'tour.dashboard.scenario.description',
      },
      onboardingSelector('dashboard-create-scenario'),
      persona,
      t,
      { popover: { side: 'top', align: 'center' }, disableActiveInteraction: false },
    ),
    buildStep(
      {
        id: 'edit',
        titleKey: isOwned
          ? 'tour.dashboard.owned.edit.title'
          : 'tour.dashboard.edit.title',
        descriptionKey: isOwned
          ? 'tour.dashboard.owned.edit.description'
          : 'tour.dashboard.edit.description',
      },
      onboardingSelector('dashboard-edit'),
      persona,
      t,
      { popover: { side: 'bottom', align: 'end' }, disableActiveInteraction: false },
    ),
  ];
  return filterStepsWithExistingElements(steps);
}

/**
 * @param {{ persona?: string, t: (key: string) => string }} ctx
 * @returns {DriveStep[]}
 */
export function getAnalysisSteps(ctx) {
  const { persona, t } = ctx;
  const steps = [
    buildStep(
      {
        id: 'intro',
        titleKey: 'tour.analysis.intro.title',
        descriptionKey: 'tour.analysis.intro.description',
      },
      undefined,
      persona,
      t,
      { popover: { side: 'over', align: 'center' } },
    ),
    buildStep(
      {
        id: 'title',
        titleKey: 'tour.analysis.title.title',
        descriptionKey: 'tour.analysis.title.description',
      },
      onboardingSelector('scenario-title'),
      persona,
      t,
      { popover: { side: 'bottom', align: 'start' } },
    ),
    buildStep(
      {
        id: 'propertyData',
        titleKey: 'tour.analysis.propertyData.title',
        descriptionKey: 'tour.analysis.propertyData.description',
      },
      onboardingSelector('scenario-property-data'),
      persona,
      t,
      { popover: { side: 'top', align: 'start' } },
    ),
    buildStep(
      {
        id: 'financing',
        titleKey: 'tour.analysis.financing.title',
        descriptionKey: 'tour.analysis.financing.description',
      },
      onboardingSelector('scenario-financing'),
      persona,
      t,
      { popover: { side: 'top', align: 'start' } },
    ),
    buildStep(
      {
        id: 'acquisition',
        titleKey: 'tour.analysis.acquisition.title',
        descriptionKey: 'tour.analysis.acquisition.description',
      },
      onboardingSelector('scenario-acquisition-costs'),
      persona,
      t,
      { popover: { side: 'top', align: 'start' } },
    ),
  ];
  return filterStepsWithExistingElements(steps);
}

/**
 * @param {{ persona?: string, t: (key: string) => string, isOwned?: boolean }} ctx
 * @returns {DriveStep[]}
 */
export function getReportSteps(ctx) {
  const { persona, t, isOwned = false } = ctx;
  const steps = [
    buildStep(
      {
        id: 'intro',
        titleKey: isOwned
          ? 'tour.report.owned.intro.title'
          : 'tour.report.intro.title',
        descriptionKey: isOwned
          ? 'tour.report.owned.intro.description'
          : 'tour.report.intro.description',
      },
      undefined,
      persona,
      t,
      { popover: { side: 'over', align: 'center' } },
    ),
    buildStep(
      {
        id: 'edit',
        titleKey: 'tour.report.edit.title',
        descriptionKey: 'tour.report.edit.description',
      },
      onboardingSelector('report-edit'),
      persona,
      t,
      { popover: { side: 'bottom', align: 'end' }, disableActiveInteraction: false },
    ),
    buildStep(
      {
        id: 'enteredInfo',
        titleKey: 'tour.report.enteredInfo.title',
        descriptionKey: isOwned
          ? 'tour.report.owned.enteredInfo.description'
          : 'tour.report.enteredInfo.description',
      },
      onboardingSelector('report-entered-info'),
      persona,
      t,
      { popover: { side: 'bottom', align: 'start' } },
    ),
    buildStep(
      {
        id: 'indicators',
        titleKey: 'tour.report.indicators.title',
        descriptionKey: isOwned
          ? 'tour.report.owned.indicators.description'
          : 'tour.report.indicators.description',
      },
      onboardingSelector('report-indicators'),
      persona,
      t,
      { popover: { side: 'bottom', align: 'start' } },
    ),
    buildStep(
      {
        id: 'financialSummary',
        titleKey: 'tour.report.financialSummary.title',
        descriptionKey: 'tour.report.financialSummary.description',
      },
      onboardingSelector('report-financial-summary'),
      persona,
      t,
      { popover: { side: 'top', align: 'start' } },
    ),
    buildStep(
      {
        id: 'financingSummary',
        titleKey: 'tour.report.financingSummary.title',
        descriptionKey: isOwned
          ? 'tour.report.owned.financingSummary.description'
          : 'tour.report.financingSummary.description',
      },
      onboardingSelector('report-financing-summary'),
      persona,
      t,
      { popover: { side: 'top', align: 'start' } },
    ),
    buildStep(
      {
        id: 'amortization',
        titleKey: 'tour.report.amortization.title',
        descriptionKey: 'tour.report.amortization.description',
      },
      onboardingSelector('report-amortization'),
      persona,
      t,
      { popover: { side: 'top', align: 'center' }, disableActiveInteraction: false },
    ),
    buildStep(
      {
        id: 'subScenarios',
        titleKey: isOwned
          ? 'tour.report.owned.subScenarios.title'
          : 'tour.report.subScenarios.title',
        descriptionKey: isOwned
          ? 'tour.report.owned.subScenarios.description'
          : 'tour.report.subScenarios.description',
      },
      onboardingSelector('report-new-scenario'),
      persona,
      t,
      { popover: { side: 'top', align: 'center' }, disableActiveInteraction: false },
    ),
    buildStep(
      {
        id: 'pdf',
        titleKey: 'tour.report.pdf.title',
        descriptionKey: 'tour.report.pdf.description',
      },
      onboardingSelector('report-pdf'),
      persona,
      t,
      { popover: { side: 'top', align: 'end' }, disableActiveInteraction: false },
    ),
    buildStep(
      {
        id: 'futureReturns',
        titleKey: 'tour.report.futureReturns.title',
        descriptionKey: isOwned
          ? 'tour.report.owned.futureReturns.description'
          : 'tour.report.futureReturns.description',
      },
      onboardingSelector('report-future-returns'),
      persona,
      t,
      { popover: { side: 'top', align: 'start' } },
    ),
  ];
  return filterStepsWithExistingElements(steps);
}

/**
 * @param {{ persona?: string, t: (key: string) => string }} ctx
 * @returns {DriveStep[]}
 */
export function getSettingsSteps(ctx) {
  const { persona, t } = ctx;
  const steps = [
    buildStep(
      {
        id: 'intro',
        titleKey: 'tour.settings.intro.title',
        descriptionKey: 'tour.settings.intro.description',
      },
      undefined,
      persona,
      t,
      { popover: { side: 'over', align: 'center' } },
    ),
    buildStep(
      {
        id: 'persona',
        titleKey: 'tour.settings.persona.title',
        descriptionKey: 'tour.settings.persona.description',
      },
      onboardingSelector('profile-persona'),
      persona,
      t,
    ),
    buildStep(
      {
        id: 'language',
        titleKey: 'tour.settings.language.title',
        descriptionKey: 'tour.settings.language.description',
      },
      onboardingSelector('profile-language'),
      persona,
      t,
      { popover: { side: 'top', align: 'start' } },
    ),
  ];
  return filterStepsWithExistingElements(steps);
}

/**
 * @param {string} tourId
 * @param {{ persona?: string, t: (key: string) => string, hasProperties?: boolean, currentStep?: string, isOwned?: boolean }} ctx
 * @returns {DriveStep[]}
 */
export function getStepsForTour(tourId, ctx) {
  switch (tourId) {
    case 'welcome':
      return getWelcomeSteps(ctx);
    case 'dashboard':
      return getDashboardSteps(ctx);
    case 'createProject':
      return getCreateProjectSteps(ctx);
    case 'analysis':
      return getAnalysisSteps(ctx);
    case 'report':
      return getReportSteps(ctx);
    case 'settings':
      return getSettingsSteps(ctx);
    default:
      return [];
  }
}
